import React from 'react';
import {
  NodeContent,
  NodeDescription,
  NodeFooter,
  NodeHeader,
  NodeTitle,
} from '@cotera/client/v0/components/ai-elements/node';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Card } from '@cotera/client/v0/components/ui/card';
import { cn } from '@cotera/client/v0/lib/utils';
import { Position } from '@xyflow/react';
import { WORKFLOW_FLOW_TRIGGER_WIDTH } from '../../workflow-flow-layout.constants';
import { WorkflowHandle } from '../workflow-handle';
import type { WorkflowTriggerHandles } from './types';
import { NODE_ACCENTS, type NodeAccent } from '../node-accent';
import { WORKFLOW_DIMMED_CONTENT_CLASS } from '../node-selection';

export type WorkflowTriggerShellProps = {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  accent?: NodeAccent;
  badge?: string;
  handles?: WorkflowTriggerHandles;
  footer?: React.ReactNode;
  /**
   * The on/off switch, rendered at the right of the body row. Sits there rather
   * than in the header because the header's title already truncates at this
   * card width, and the body line is mostly empty space.
   */
  enabledToggle?: React.ReactNode;
  /**
   * Fades the card's header and body for a trigger that is switched off. The
   * switch itself stays lit — see {@link WORKFLOW_DIMMED_CONTENT_CLASS}.
   */
  dimmed?: boolean;
  children?: React.ReactNode;
  className?: string;
};

/** Triggers are graph entry points: source only by default. */
const DEFAULT_TRIGGER_HANDLES: WorkflowTriggerHandles = {
  target: false,
  source: true,
};

/**
 * Shared chrome for workflow triggers. Type-specific fields use a
 * {@link WorkflowTriggerRenderStrategy}.
 */
export const WorkflowTriggerShell: React.FC<WorkflowTriggerShellProps> = ({
  title,
  description,
  icon,
  accent = 'trigger',
  badge,
  handles = DEFAULT_TRIGGER_HANDLES,
  footer,
  enabledToggle,
  dimmed = false,
  children,
  className,
}) => {
  const hasBody =
    (enabledToggle !== undefined && enabledToggle !== null) ||
    (children !== undefined && children !== null);
  const hasFooter = footer !== undefined && footer !== null;
  const accentStyle = NODE_ACCENTS[accent];

  return (
    <Card
      className={cn(
        'node-container relative h-auto gap-0 rounded-2xl border-primary/20 p-0 shadow-xs transition-[color,box-shadow,border-color]',
        className
      )}
      style={{ width: WORKFLOW_FLOW_TRIGGER_WIDTH }}
    >
      {handles.target ? (
        <WorkflowHandle position={Position.Top} type="target" />
      ) : null}
      {handles.source ? (
        <WorkflowHandle position={Position.Bottom} type="source" />
      ) : null}
      {/* Flat single-colour card, as on `WorkflowNodeShell` — the primitive's own
          `bg-secondary` and `border-b` plus the trigger's `bg-primary/5` wash all
          come off. The card keeps its `border-primary/20` so a trigger still reads
          as the entry point without a tinted band. */}
      <NodeHeader
        className={cn(
          'p-3! rounded-t-2xl bg-transparent border-b-0',
          dimmed && WORKFLOW_DIMMED_CONTENT_CLASS
        )}
      >
        {/* Centred against the whole text block, and the badge opted out with
            `self-start` — see `WorkflowNodeShell` for the reasoning. */}
        <div className="flex items-center gap-2.5 min-w-0 w-full">
          {icon !== undefined && icon !== null ? (
            <div
              className={cn(
                'shrink-0 flex h-9 w-9 items-center justify-center rounded-lg [&_svg]:size-5',
                accentStyle.chip
              )}
            >
              {icon}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            {/* `leading-7` is for rhythm, not alignment — see
                `WorkflowNodeShell` for the reasoning. */}
            <NodeTitle className="text-sm font-medium leading-7 truncate">
              {title}
            </NodeTitle>
            {description !== undefined && description !== '' ? (
              <NodeDescription className="text-xs truncate">
                {description}
              </NodeDescription>
            ) : null}
          </div>
          {badge !== undefined && badge !== '' ? (
            <div className="flex h-7 shrink-0 items-center self-start">
              <Badge variant={accentStyle.badge} size="sm">
                {badge}
              </Badge>
            </div>
          ) : null}
        </div>
      </NodeHeader>
      {hasBody ? (
        <NodeContent>
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'min-w-0 flex-1',
                dimmed && WORKFLOW_DIMMED_CONTENT_CLASS
              )}
            >
              {children}
            </div>
            {enabledToggle}
          </div>
        </NodeContent>
      ) : null}
      {hasFooter ? (
        <NodeFooter className="rounded-b-2xl bg-transparent">
          {footer}
        </NodeFooter>
      ) : null}
    </Card>
  );
};
